<template>
  <ModalRoot
    #body="{ close, isOpen }"
    v-model="isOpen"
    class="data-[modal-wrapper]:*:!w-full"
  >
    <ModalBody
      :is-open="isOpen"
      hide-close-button
      class="!p-0 !bg-brand-light-bg-overlay-50 rounded-[40px] overflow-hidden !max-w-[590px]"
      @close="close"
    >
      <div
        class="p-3 md:p-6 bg-fill-brand-secondary rounded-[40px] backdrop-blur-[70px] flex flex-col justify-start items-center gap-6"
      >
        <div class="h-10 justify-between items-center flex w-full">
          <div
            class="p-2 bg-fill-brand-secondary rounded-full backdrop-blur-[70px] justify-center items-center gap-1 flex"
          >
            <div class="text-center flex gap-1">
              <span
                class="text-white/80 text-sm font-medium font-sans leading-tight"
              >
                {{
                  $t("channel.leaderboard.members.count", {
                    count: formatCompactNumber(
                      channelStore.channel?.members_count ?? 0,
                      userStore.userLocale,
                    ),
                  })
                }}
              </span>
            </div>
          </div>
          <div
            class="w-10 h-10 bg-fill-brand-secondary rounded-full backdrop-blur-[35px] flex justify-center items-center cursor-pointer"
            :aria-label="$t('channel.leaderboard.close')"
            @click="close()"
          >
            <Icon icon="mdi:close" class="size-6 text-white" />
          </div>
        </div>

        <TabsRoot
          v-model="activeTab"
          :default-value="LeaderboardLength.Monthly"
          class="flex flex-col w-full"
        >
          <div
            class="bg-brand-bgdivider-10 rounded-3xl border-[3px] border-brand-primary flex-col justify-start items-center inline-flex overflow-hidden"
          >
            <div
              class="p-4 bg-brand-bgdivider-10 self-stretch justify-end items-start gap-3 flex flex-col"
            >
              <div class="flex flex-row gap-3">
                <div
                  class="self-stretch p-1.5 pb-2 bg-brand-surface-hover rounded-full flex-col justify-between items-center inline-flex"
                >
                  <img
                    :src="channelLogo"
                    :alt="$t('channel.leaderboard.title')"
                    class="size-8 min-w-8 rounded-full border-2 border-brand-primary"
                  />
                  <div class="flex-col justify-start items-center flex">
                    <Icon icon="mdi:people" class="w-4 h-4 text-[#d9d9d9]" />
                  </div>
                </div>
                <ChannelMonthlyPrize
                  mini
                  size="sm"
                  :leaderboard-length="activeTab"
                />
              </div>
              <div
                class="self-stretch justify-end items-start inline-flex w-full"
              >
                <TabsList
                  :aria-label="$t('channel.leaderboard.title')"
                  class="relative px-1 flex bg-brand-bgdivider-20 rounded-3xl text-base text-basics-primary font-serif w-full"
                >
                  <TabsIndicator
                    class="absolute py-1 left-0 h-full bottom-0 top-0 w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] transition-[width,transform] duration-300"
                  >
                    <div class="bg-brand-primary h-full rounded-2xl" />
                  </TabsIndicator>

                  <TabsTrigger
                    class="uppercase px-4 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer w-1/2 whitespace-nowrap"
                    :value="LeaderboardLength.Monthly"
                  >
                    {{ $t("channel.mvpLeaderboard.tabs.monthly") }}
                  </TabsTrigger>
                  <TabsTrigger
                    class="uppercase px-4 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer w-1/2 whitespace-nowrap"
                    :value="LeaderboardLength.Weekly"
                  >
                    {{ $t("channel.mvpLeaderboard.tabs.weekly") }}
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            <div class="w-full">
              <ContestLeaderboard
                :loading="loading"
                :ranking="ranking"
                :leaderboard-length="activeTab"
                :loading-text="$t('channel.leaderboard.loading')"
                :load-more-text="$t('channel.leaderboard.loadMore')"
                :empty-text="$t('channel.leaderboard.empty')"
                @load-more="loadMore"
              />
            </div>
          </div>
        </TabsRoot>

        <div
          v-if="
            userStore.isAuthenticated &&
            channelStore.hasSuperfanProduct &&
            !channelStore.isSuperfan &&
            !channelStore.isHost
          "
          class="flex flex-col items-stretch justify-center gap-3 p-4"
        >
          <div class="flex-1 flex items-center gap-2">
            <CoinPile class="w-12 h-16 md:h-11 md:w-8" />
            <div class="flex flex-col">
              <h3 class="text-lg text-text-on-brand font-serif">
                {{ $t("channel.mvp.multiplier.title") }}
              </h3>
              <p class="text-text text-sm">
                {{ $t("channel.mvp.multiplier.description") }}
              </p>
            </div>
          </div>
          <UnlockPointsMultiplierButton @click="close" />
        </div>

        <GetChannelMembershipButton />
      </div>
    </ModalBody>
  </ModalRoot>
</template>

<script setup lang="ts">
import ModalBody from "@/components/ui/modal/modal-body.vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import { TabsIndicator, TabsList, TabsRoot, TabsTrigger } from "reka-ui";
import { computed, ref, watch } from "vue";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import { useChannelStore } from "@/stores/channel.store";
import { Icon } from "@iconify/vue";
import { useQuery } from "@vue/apollo-composable";
import { ChannelLeaderboardDocument } from "@/generated/graphql";
import ContestLeaderboard from "@/components/contest/contest-leaderboard.vue";
import ChannelMonthlyPrize from "@/components/channel/channel-monthly-prize.vue";
import { useUserStore } from "@/stores/user.store";
import { formatCompactNumber } from "@/utils/format";
import GetChannelMembershipButton from "@/components/channel/channel-page/get-channel-membership-button.vue";
import { LeaderboardLength } from "@f3/shared/leaderboard-types";
import { AppThemeKind, useThemeStore } from "@/stores/theme.store";

import CoinPile from "../ui/icon/coin-pile.vue";
import UnlockPointsMultiplierButton from "../ui/button/unlock-points-multiplier-button.vue";

const isOpen = defineModel<boolean>("isOpen", { required: true });

const channelStore = useChannelStore();
const userStore = useUserStore();
const themeStore = useThemeStore();
const activeTab = ref(LeaderboardLength.Monthly);

const page = ref(0);
const LIMIT = 20;
const ranking = ref<any[]>([]);

useJustOneOpenModal(isOpen);

const channelLogo = computed(() => channelStore.channel?.logo ?? undefined);

const {
  result: channelLeaderboardResult,
  loading,
  refetch,
  onResult,
} = useQuery(
  ChannelLeaderboardDocument,
  computed(() => ({
    channel_id: channelStore.channelSlug!,
    leaderboard_length:
      activeTab.value === LeaderboardLength.Monthly
        ? LeaderboardLength.Monthly
        : LeaderboardLength.Weekly,
    highlights: false,
    page: page.value,
    limit: LIMIT,
    community_id:
      themeStore.currentTheme?.kind === AppThemeKind.community
        ? themeStore.currentTheme?.id
        : undefined,
  })),
  {
    enabled: computed(() => !!channelStore.channelSlug),
    fetchPolicy: "network-only",
  },
);

// Apollo can return the same object reference when a cache read produces
// structurally identical data, so a `watch(result.value, …)` fails to fire
// on repeat opens. `onResult` runs on every observable emission regardless.
onResult(({ data }) => {
  if (data?.channelLeaderboard?.__typename !== "Leaderboard") return;
  ranking.value =
    page.value === 0
      ? [...data.channelLeaderboard.ranking]
      : [...ranking.value, ...data.channelLeaderboard.ranking];
});

const loadMore = () => {
  if (loading.value) return;

  const totalContestants =
    channelLeaderboardResult.value?.channelLeaderboard?.__typename ===
    "Leaderboard"
      ? channelLeaderboardResult.value.channelLeaderboard.total_contestants
      : 0;

  if (ranking.value.length >= totalContestants) return;

  page.value++;
};

watch(
  () => activeTab.value,
  () => {
    // Variable change (leaderboard_length) auto-triggers the query; we only
    // need to clear local state so the next response replaces rather than appends.
    page.value = 0;
    ranking.value = [];
  },
);

watch(isOpen, (nowOpen) => {
  if (!nowOpen) return;
  const wasPaginated = page.value !== 0;
  page.value = 0;
  ranking.value = [];
  // If page was already 0, resetting it doesn't change the query variables,
  // so Apollo won't auto-refetch — we have to ask explicitly. Otherwise the
  // variable change already drives a refetch and calling refetch() here would
  // duplicate the request.
  if (!wasPaginated) refetch();
});
</script>
